import { Redirect, router } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import { capitalize } from 'lodash';
import * as React from 'react';
import {
    BodyLargeRegular,
    BodyMediumEmphasized,
    BodyMediumRegular,
    H2Regular,
    Private,
    XStack,
    YStack,
} from 'smoothie';

import PopUpReceipt from '../../../../common/components/PopUpReceipt';
import {
    format,
    formatWithoutTimezone,
    SHORT_MONTH_DATE_FORMAT_SLASH,
    TIME_FORMAT,
} from '../../../../common/utils/dateUtils';
import LogRocketFacade from '../../../../logger/log-rocket/LogRocketFacade';
import { captureMessage } from '../../../../logger/Logger';
import {
    selectedSpaceAtom,
    selectedTransactionAtom,
} from '../../../state/SpacesAtoms';
import { TRANSACTION_TYPES_DESCRIPTION_MAP } from '../../../types/Transactions';
import { LegacyTransactionIcons } from './LegacyTransactionIcons';

const TransactionDetailsModal = () => {
    LogRocketFacade.tagPage('TransactionDetailsModal');

    // If we're in this component, we expect a space to have been selected
    // Use this to show the account icon since transactions do not have account details on them
    const selectedSpace = useAtomValue(selectedSpaceAtom);
    const [selectedTransaction, setSelectedTransaction] = useAtom(
        selectedTransactionAtom,
    );

    if (selectedTransaction == null || selectedSpace == null) {
        captureMessage('Missing transaction or space for details modal');
        return <Redirect href="/home/banking" />;
    }

    const {
        dcSign,
        description,
        type,
        created,
        postedDate,
        formattedAmount,
        status,
        formattedBalanceAfterTransaction,
    } = selectedTransaction;

    return (
        <PopUpReceipt
            title="Transaction details"
            onClose={() => {
                setSelectedTransaction(null);
                router.back();
            }}
        >
            <YStack marginBottom={16}>
                <YStack alignItems="center" gap={8}>
                    <LegacyTransactionIcons
                        account={selectedSpace}
                        dcSign={dcSign}
                        iconBorderRadius={100}
                    />
                    <BodyLargeRegular textAlign="center">
                        {description}
                    </BodyLargeRegular>
                    <Private>
                        <H2Regular>{formattedAmount}</H2Regular>
                    </Private>
                </YStack>
                <TransactionDetailItem
                    label="Status"
                    value={capitalize(status)}
                />
                <Private>
                    <TransactionDetailItem
                        label="Balance"
                        value={formattedBalanceAfterTransaction}
                    />
                </Private>
                <TransactionDetailItem
                    label="Deposit method"
                    value={TRANSACTION_TYPES_DESCRIPTION_MAP[type]}
                />
                <TransactionDetailItem
                    label="Transaction date"
                    value={format(
                        created,
                        `${TIME_FORMAT}, ${SHORT_MONTH_DATE_FORMAT_SLASH}`,
                    )}
                />
                <TransactionDetailItem
                    label="Date posted"
                    value={formatWithoutTimezone(
                        postedDate,
                        SHORT_MONTH_DATE_FORMAT_SLASH,
                    )}
                />
            </YStack>
        </PopUpReceipt>
    );
};

interface TransactionDetailItemProps {
    label: string;
    value: string;
}

const TransactionDetailItem = ({
    label,
    value,
}: TransactionDetailItemProps) => {
    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            gap={8}
            paddingVertical={16}
            borderBottomWidth={1}
            borderBottomColor="$gray100"
        >
            <BodyMediumRegular color="$gray900">{label}</BodyMediumRegular>
            <BodyMediumEmphasized
                numberOfLines={2}
                maxWidth={200}
                textAlign="right"
            >
                {value}
            </BodyMediumEmphasized>
        </XStack>
    );
};

export default TransactionDetailsModal;
